import { useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  toggleConnectionStatus,
  disconnectPlatformConnection,
  setPlatformConnectionDefault
} from "../api/accounting.api";
import { errorSnackBar, successSnackBar } from "../actions/common.action";
import {
  fetchAccountingConnections,
  setSelectedConnectionId,
  updateConnectionStatusSuccess,
  clearAccountingState
} from "../slice/accounting.slice";

/**
 * Custom hook to manage platform accounting connections, selection, and status syncing.
 * @param {string} accountId - User Account UUID
 * @param {Function} [selectConnection] - Optional callback triggered when selected connection changes
 * @returns {Object} Connections state, loading indicator, and platform action handlers
 */
export function useAccountingConnections(accountId, selectConnection) {
  const dispatch = useDispatch();
  const featureFlags = useSelector((state) => state.user?.featureFlags) || {};
  const { connections: rawConnections, selectedConnectionId, loading } = useSelector((state) => state.accounting || {});

  const entitlements = useSelector((state) => state.user?.entitlements) || {};
  const connections = (rawConnections || []).filter(c => {
    const platformKey = (c?.platform || '').toLowerCase();
    let entitlementKey = 'xeroIntegration';
    let flagKey = 'xero_integration';
    if (platformKey === 'quickbooks' || platformKey === 'qbo') {
      entitlementKey = 'qboIntegration';
      flagKey = 'quickbooks_integration';
    } else if (platformKey === 'myob') {
      entitlementKey = 'myobIntegration';
      flagKey = 'myob_integration';
    }
    const hasEntitlement = entitlements[entitlementKey] !== false;
    const hasFlag = featureFlags[flagKey] !== false;
    return hasEntitlement && hasFlag;
  });

  const selectedConnection = connections.find(connection => connection.id === selectedConnectionId)
    || (connections.length > 0 ? connections[0] : null);

  const selectConnectionRef = useRef(selectConnection);
  selectConnectionRef.current = selectConnection;

  // Notify parent onSelect callback when selectedConnection changes, preventing circular recursion
  const prevSelectedIdRef = useRef(null);
  useEffect(() => {
    if (selectedConnection && selectedConnection.id !== prevSelectedIdRef.current) {
      prevSelectedIdRef.current = selectedConnection.id;
      if (typeof selectConnectionRef.current === 'function') {
        selectConnectionRef.current(selectedConnection);
      }
    }
  }, [selectedConnection]);

  const setSelectedConnection = useCallback((conn) => {
    const connId = conn ? (typeof conn === 'object' ? conn.id : conn) : null;
    dispatch(setSelectedConnectionId(connId));
  }, [dispatch]);

  const fetchConnections = useCallback(async (forceRefresh = true, useOverlay = true) => {
    if (!accountId) return;
    try {
      await dispatch(fetchAccountingConnections(forceRefresh, useOverlay));
    } catch {
      // Error logging & notifications managed in thunk
    }
  }, [accountId, dispatch]);


  
  const handleError = useCallback((err, defaultMsg) => {
    dispatch(errorSnackBar(err?.message || defaultMsg));
  }, [dispatch]);

  const handleDisconnect = useCallback(async (connection) => {
    if (!connection?.id) return;
    try {
      await disconnectPlatformConnection(connection.id);
      dispatch(successSnackBar('Disconnected successfully'));
      await fetchConnections(true);
    } catch (err) {
      handleError(err, 'Failed to disconnect integration');
    }
  }, [fetchConnections, dispatch]);

  const handleToggleActive = useCallback(async (connection, e) => {
    if (!connection?.id) return;
    const isActive = Boolean(e?.target?.checked);
    try {
      const response = await toggleConnectionStatus(connection.id, isActive);
      const updatedIsActive = (response && response.data && typeof response.data.is_active === 'boolean')
        ? response.data.is_active
        : isActive;

      dispatch(updateConnectionStatusSuccess({ id: connection.id, is_active: updatedIsActive }));
    } catch (err) {
      const isSyncLock = err?.response?.data?.errorCode === 'SYNC_LOCK' || err?.response?.status === 423;
      if (isSyncLock) {
        dispatch(errorSnackBar('This connection is currently syncing. Settings are read-only until the sync completes.'));
      } else {
        handleError(err, 'Failed to toggle active status');
      }
    }
  }, [dispatch]);

  const handleSetDefault = useCallback(async (conn) => {
    if (!conn?.id) return;
    try {
      await setPlatformConnectionDefault(conn.id);
      await fetchConnections(true);
    } catch (err) {
      handleError(err, 'Failed to set default connection');
    }
  }, [fetchConnections, dispatch]);

  const prevAccountIdRef = useRef(accountId);
  useEffect(() => {
    if (accountId && (connections.length === 0 || accountId !== prevAccountIdRef.current)) {
      const isAccountChanged = accountId !== prevAccountIdRef.current;
      prevAccountIdRef.current = accountId;
      if (isAccountChanged) {
        dispatch(clearAccountingState());
      }
      fetchConnections(true, true);
    }
  }, [accountId, connections.length, fetchConnections, dispatch]);

  const fetchConnectionsRef = useRef(fetchConnections);
  fetchConnectionsRef.current = fetchConnections;
  const isSyncing = connections.some(c => c.is_syncing === true);

  useEffect(() => {
    if (!isSyncing) return;

    const interval = setInterval(() => {
      if (fetchConnectionsRef.current) {
        fetchConnectionsRef.current(false);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [isSyncing]);

  return {
    loading,
    connections,
    selectedConnection,
    setSelectedConnection,
    fetchConnections,
    handleDisconnect,
    handleToggleActive,
    handleSetDefault
  };

}
